import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { breadcrumbSchema } from '../utils/schemas';

const articles = [
  {
    title: 'First Home Buyer Guide Australia 2026',
    description:
      'Everything you need to know about buying your first home in Australia — from deposits and government grants to the step-by-step buying process.',
    path: '/blog/first-home-buyer-guide',
    readTime: 12,
    date: '2026-03-15',
    category: 'First Home Buyers',
  },
  {
    title: 'When Should You Refinance Your Home Loan?',
    description:
      'Learn how to identify the right time to refinance, calculate your break-even point, and understand whether switching lenders could save you money.',
    path: '/blog/when-to-refinance',
    readTime: 10,
    date: '2026-03-10',
    category: 'Refinancing',
  },
  {
    title: 'Fixed vs Variable Home Loans: Which Is Right for You?',
    description:
      'A comprehensive comparison of fixed and variable rate home loans in Australia, including split loan options and how to choose based on your circumstances.',
    path: '/blog/fixed-vs-variable',
    readTime: 11,
    date: '2026-02-28',
    category: 'Home Loans',
  },
  {
    title: 'How Much Can You Borrow? Understanding Your Borrowing Power',
    description:
      'Discover how lenders assess your borrowing capacity, what affects your serviceability, and practical steps to maximise how much you can borrow.',
    path: '/blog/borrowing-power',
    readTime: 10,
    date: '2026-02-20',
    category: 'Home Loans',
  },
  {
    title: 'Mortgage Broker vs Bank: Which Should You Choose?',
    description:
      'An honest comparison of using a mortgage broker versus going directly to a bank, including costs, access to products, and the pros and cons of each approach.',
    path: '/blog/broker-vs-bank',
    readTime: 9,
    date: '2026-02-12',
    category: 'Industry Insights',
  },
  {
    title: 'Costco Home Loans Australia: Member Benefits Explained',
    description:
      'A detailed guide to how Costco Executive Members can access exclusive home loan rates and earn Costco Shop Card rewards through Manage Your Loans.',
    path: '/blog/costco-home-loans-guide',
    readTime: 10,
    date: '2026-02-05',
    category: 'Costco Partnership',
  },
];

const Blog = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blog — Home Loan Tips & Guides"
        description="Expert insights on home loans, refinancing, first home buying, and property finance in Australia from the Manage Your Loans team."
        path="/blog"
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
          ]),
        ]}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#00594C] to-[#003d35]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl mb-6 border border-white/20">
              <BookOpen className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Home Loan Insights
            </h1>
            <p className="text-xl md:text-2xl text-gray-200">
              Expert guides and tips to help you make smarter mortgage decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Article Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, i) => (
              <Link
                key={i}
                to={article.path}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="h-2 bg-gradient-to-r from-[#00594C] to-[#99DF63]" />
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs font-semibold text-[#99DF63] uppercase tracking-wide mb-2">
                    {article.category}
                  </span>
                  <h2 className="text-lg font-bold text-[#00594C] group-hover:text-[#003d35] transition-colors mb-3 leading-snug">
                    {article.title}
                  </h2>
                  <p className="text-sm text-gray-600 mb-4 flex-1 leading-relaxed">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="inline-flex items-center space-x-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{article.readTime} min read</span>
                    </span>
                    <span className="inline-flex items-center space-x-1 text-[#00594C] font-semibold group-hover:translate-x-1 transition-transform">
                      <span>Read</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
